import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multiselect';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { SaveFilterModal } from './SaveFilterModal';
import { SavedFiltersDropdown } from './SavedFiltersDropdown';
import { toast } from 'sonner';
import { savedFiltersService } from '@/services/saved-filters-service';
import { dataService, isDemoMode } from '@/services/data-service';
import type { PendingFilters, SavedFilter } from '@/contexts/RightPanelContext';
import type { Location, Category } from '@/types/settings';

const INSURANCE_VALUE_STATUSES = [
  { label: 'Open', value: 'active' },
  { label: 'Part Disposed', value: 'outOfService' },
];
const FALLBACK_CATEGORIES = ['IT Equipment', 'Furniture', 'Vehicles', 'Machinery', 'Buildings', 'Land'];
const FALLBACK_LOCATIONS = ['Head Office', 'Warehouse A', 'Warehouse B', 'Branch 1', 'Branch 2', 'Remote'];

const createEmptyFilters = (): Partial<PendingFilters> => ({
  selectedCategories: [],
  selectedStatuses: [],
  selectedLocations: [],
  acquisitionDateRange: { from: undefined, to: undefined },
  acquisitionCostMin: '',
  acquisitionCostMax: '',
  netBookValueMin: '',
  netBookValueMax: '',
  selectedAssignedTo: [],
  selectedDepreciationMethods: [],
  warrantyExpiryDateRange: { from: undefined, to: undefined },
  selectedManufacturers: [],
  selectedSubCategories: [],
  selectedSubLocations: [],
});

export const InsuranceValueFiltersTab: React.FC = () => {
  const {
    setActiveFilterCount,
    pendingFilters,
    setPendingFilters,
    appliedFilters,
    applyFilters,
    clearAllFilters,
    savedFilters,
    setSavedFilters,
    loadSavedFilter,
  } = useRightPanel();

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [purchaseDateRange, setPurchaseDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  const [insuranceValueMin, setInsuranceValueMin] = useState('');
  const [insuranceValueMax, setInsuranceValueMax] = useState('');

  const [apiLocations, setApiLocations] = useState<Location[]>([]);
  const [apiCategories, setApiCategories] = useState<Category[]>([]);
  const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(false);

  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null);
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null);
  const [, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isLoadingFromExternal = React.useRef(false);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      setIsLoadingFilterOptions(true);
      try {
        const [locations, categories] = await Promise.all([
          dataService.getLocations(),
          dataService.getCategories(),
        ]);
        setApiLocations(locations);
        setApiCategories(categories);
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      } finally {
        setIsLoadingFilterOptions(false);
      }
    };
    fetchFilterOptions();
  }, []);

  const locationOptions = useMemo(() => {
    if (apiLocations.length > 0) {
      return apiLocations.map(loc => ({ label: loc.name, value: loc.name }));
    }
    if (isDemoMode()) {
      return FALLBACK_LOCATIONS.map(loc => ({ label: loc, value: loc }));
    }
    return [];
  }, [apiLocations]);

  const categoryOptions = useMemo(() => {
    if (apiCategories.length > 0) {
      return apiCategories.map(cat => ({ label: cat.description ?? cat.code, value: cat.description ?? cat.code }));
    }
    if (isDemoMode()) {
      return FALLBACK_CATEGORIES.map(cat => ({ label: cat, value: cat }));
    }
    return [];
  }, [apiCategories]);

  const statusOptions = useMemo(() => {
    return INSURANCE_VALUE_STATUSES.map(status => ({
      label: status.label,
      value: status.value,
    }));
  }, []);

  const loadSavedFiltersFromApi = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = await savedFiltersService.getSavedFilters();
      setSavedFilters(filters);
    } catch (error) {
      console.error('Failed to load saved filters:', error);
      toast.error('Failed to load saved filters');
    } finally {
      setIsLoading(false);
    }
  }, [setSavedFilters]);

  useEffect(() => {
    loadSavedFiltersFromApi();
  }, [loadSavedFiltersFromApi]);

  useEffect(() => {
    if (isLoadingFromExternal.current) {
      return;
    }

    const filters: Partial<PendingFilters> = {
      selectedCategories,
      selectedStatuses,
      selectedLocations,
      acquisitionDateRange: purchaseDateRange,
      acquisitionCostMin: '',
      acquisitionCostMax: '',
      netBookValueMin: '',
      netBookValueMax: '',
      selectedAssignedTo: [],
      selectedDepreciationMethods: [],
      warrantyExpiryDateRange: { from: undefined, to: undefined },
      selectedManufacturers: [],
      selectedSubCategories: [],
      selectedSubLocations: [],
      amountMin: insuranceValueMin || undefined,
      amountMax: insuranceValueMax || undefined,
    };
    setPendingFilters(filters as PendingFilters);
  }, [
    selectedCategories,
    selectedStatuses,
    selectedLocations,
    purchaseDateRange,
    insuranceValueMin,
    insuranceValueMax,
    setPendingFilters,
  ]);

  useEffect(() => {
    if (pendingFilters) {
      isLoadingFromExternal.current = true;
      setSelectedCategories(pendingFilters.selectedCategories ?? []);
      setSelectedStatuses(pendingFilters.selectedStatuses ?? []);
      setSelectedLocations(pendingFilters.selectedLocations ?? []);
      setPurchaseDateRange(pendingFilters.acquisitionDateRange ?? { from: undefined, to: undefined });
      setInsuranceValueMin(pendingFilters.amountMin ?? '');
      setInsuranceValueMax(pendingFilters.amountMax ?? '');
      setTimeout(() => {
        isLoadingFromExternal.current = false;
      }, 0);
    }
  }, [pendingFilters]);

  useEffect(() => {
    if (!pendingFilters && !appliedFilters) {
      isLoadingFromExternal.current = true;
      setSelectedCategories([]);
      setSelectedStatuses([]);
      setSelectedLocations([]);
      setPurchaseDateRange({ from: undefined, to: undefined });
      setInsuranceValueMin('');
      setInsuranceValueMax('');
      setSelectedSavedFilterId(null);
      setTimeout(() => {
        isLoadingFromExternal.current = false;
      }, 0);
    }
  }, [pendingFilters, appliedFilters]);

  const handleClearAll = () => {
    isLoadingFromExternal.current = true;
    setSelectedCategories([]);
    setSelectedStatuses([]);
    setSelectedLocations([]);
    setPurchaseDateRange({ from: undefined, to: undefined });
    setInsuranceValueMin('');
    setInsuranceValueMax('');
    setSelectedSavedFilterId(null);
    clearAllFilters();
    setTimeout(() => {
      isLoadingFromExternal.current = false;
    }, 0);
  };

  const activeFilterCount =
    selectedCategories.length +
    selectedStatuses.length +
    selectedLocations.length +
    (purchaseDateRange.from || purchaseDateRange.to ? 1 : 0) +
    (insuranceValueMin || insuranceValueMax ? 1 : 0);

  useEffect(() => {
    setActiveFilterCount(activeFilterCount);
  }, [activeFilterCount, setActiveFilterCount]);

  useEffect(() => {
    if (appliedFilters === null && pendingFilters === null && activeFilterCount > 0 && !isLoadingFromExternal.current) {
      const emptyFilters = createEmptyFilters();
      setSelectedCategories(emptyFilters.selectedCategories ?? []);
      setSelectedStatuses(emptyFilters.selectedStatuses ?? []);
      setSelectedLocations(emptyFilters.selectedLocations ?? []);
      setPurchaseDateRange({ from: undefined, to: undefined });
      setInsuranceValueMin('');
      setInsuranceValueMax('');
      setSelectedSavedFilterId(null);
    }
  }, [appliedFilters, pendingFilters, activeFilterCount]);

  const hasFilterChanges = () => {
    if (!appliedFilters && activeFilterCount === 0) return false;
    if (!appliedFilters && activeFilterCount > 0) return true;
    if (appliedFilters && activeFilterCount === 0) return true;
    if (!pendingFilters) return false;
    return JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters);
  };

  const handleApplyFilters = () => {
    applyFilters();
  };

  const handleSaveAndApply = () => {
    if (activeFilterCount === 0) {
      toast.error('Please configure at least one filter before saving');
      return;
    }
    setSaveModalOpen(true);
  };

  const handleSaveFilter = async (name: string) => {
    if (!pendingFilters) return;

    setIsSaving(true);
    try {
      if (editingFilter) {
        const updatedFilter = await savedFiltersService.updateSavedFilter(
          editingFilter.id,
          name,
          pendingFilters
        );
        if (updatedFilter) {
          const updatedFilters = savedFilters.map((filter) =>
            filter.id === editingFilter.id ? updatedFilter : filter
          );
          setSavedFilters(updatedFilters);
          toast.success(`Filter '${name}' updated successfully`);
        } else {
          toast.error('Filter not found');
        }
        setEditingFilter(null);
      } else {
        const newFilter = await savedFiltersService.createSavedFilter(name, pendingFilters);
        setSavedFilters([...savedFilters, newFilter]);
        toast.success(`Filter '${name}' saved successfully`);
        setSelectedSavedFilterId(newFilter.id);
      }
      applyFilters();
    } catch (error) {
      console.error('Failed to save filter:', error);
      toast.error('Failed to save filter');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectSavedFilter = (filterId: string | null) => {
    setSelectedSavedFilterId(filterId);
    if (filterId) {
      loadSavedFilter(filterId);
    } else {
      handleClearAll();
    }
  };

  const handleEditFilter = (filter: SavedFilter) => {
    setEditingFilter(filter);
    setSaveModalOpen(true);
  };

  const handleDeleteFilter = async (filterId: string) => {
    try {
      const success = await savedFiltersService.deleteSavedFilter(filterId);
      if (success) {
        setSavedFilters(savedFilters.filter((f) => f.id !== filterId));
        if (selectedSavedFilterId === filterId) {
          setSelectedSavedFilterId(null);
        }
        toast.success('Filter deleted successfully');
      } else {
        toast.error('Filter not found');
      }
    } catch (error) {
      console.error('Failed to delete filter:', error);
      toast.error('Failed to delete filter');
    }
  };

  const handleDuplicateFilter = async (filter: SavedFilter) => {
    try {
      const duplicatedFilter = await savedFiltersService.createSavedFilter(
        `${filter.name} (Copy)`,
        filter.filters
      );
      setSavedFilters([...savedFilters, duplicatedFilter]);
      toast.success(`Filter '${duplicatedFilter.name}' created`);
      setSelectedSavedFilterId(duplicatedFilter.id);
      loadSavedFilter(duplicatedFilter.id);
    } catch (error) {
      console.error('Failed to duplicate filter:', error);
      toast.error('Failed to duplicate filter');
    }
  };

  useEffect(() => {
    if (!pendingFilters || activeFilterCount === 0) {
      if (selectedSavedFilterId) {
        setSelectedSavedFilterId(null);
      }
      return;
    }

    if (selectedSavedFilterId) {
      const savedFilter = savedFilters.find((f) => f.id === selectedSavedFilterId);
      if (savedFilter && JSON.stringify(savedFilter.filters) !== JSON.stringify(pendingFilters)) {
        setSelectedSavedFilterId(null);
      }
    }
  }, [pendingFilters, activeFilterCount, selectedSavedFilterId, savedFilters]);

  const hasUnsavedChanges = hasFilterChanges();

  return (
    <div className="flex flex-col h-full">
      <div className="border-b bg-[#f8fafc] dark:bg-[#0f172b]" style={{ borderColor: '#eee' }}>
        <SavedFiltersDropdown
          savedFilters={savedFilters}
          selectedFilterId={selectedSavedFilterId}
          onSelectFilter={handleSelectSavedFilter}
          onEditFilter={handleEditFilter}
          onDeleteFilter={handleDeleteFilter}
          onDuplicateFilter={handleDuplicateFilter}
        />
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-24">

        {/* Category Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Category</Label>
              {selectedCategories.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedCategories.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Filter assets by category</p>
          </div>

          <MultiSelect
            options={categoryOptions}
            value={selectedCategories}
            onValueChange={setSelectedCategories}
            placeholder={isLoadingFilterOptions ? "Loading..." : "Select categories..."}
          />
        </div>

        <Separator />

        {/* Location Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Location</Label>
              {selectedLocations.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedLocations.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Physical location or site</p>
          </div>

          <MultiSelect
            options={locationOptions}
            value={selectedLocations}
            onValueChange={setSelectedLocations}
            placeholder={isLoadingFilterOptions ? "Loading..." : "Select locations..."}
          />
        </div>

        <Separator />

        {/* Status Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Status</Label>
              {selectedStatuses.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedStatuses.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Asset status (Open or Part Disposed)</p>
          </div>

          <MultiSelect
            options={statusOptions}
            value={selectedStatuses}
            onValueChange={setSelectedStatuses}
            placeholder="Select statuses..."
          />
        </div>

        <Separator />

        {/* Purchase Date Range Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Purchase Date</Label>
              {(purchaseDateRange.from || purchaseDateRange.to) && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  1
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Date when asset was purchased</p>
          </div>

          <DateRangePicker
            dateRange={purchaseDateRange}
            onDateRangeChange={(range) => setPurchaseDateRange({ from: range?.from, to: range?.to })}
          />
        </div>

        <Separator />

        {/* Insurance Value Range Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-sm font-semibold">Insurance Value</Label>
            <p className="text-xs text-muted-foreground">Filter by insurance value range</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="insurance-value-min" className="text-xs text-muted-foreground">Min</Label>
              <Input
                id="insurance-value-min"
                type="number"
                placeholder="0"
                value={insuranceValueMin}
                onChange={(e) => setInsuranceValueMin(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="insurance-value-max" className="text-xs text-muted-foreground">Max</Label>
              <Input
                id="insurance-value-max"
                type="number"
                placeholder="999999"
                value={insuranceValueMax}
                onChange={(e) => setInsuranceValueMax(e.target.value)}
                className="h-9"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 bg-background border-t shadow-lg p-4">
        {hasUnsavedChanges && (
          <div className="mb-3 text-center">
            <Badge variant="secondary" className="text-xs">
              Unsaved changes
            </Badge>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSaveAndApply}
            disabled={activeFilterCount === 0 || isSaving}
            className="flex-1"
          >
            {isSaving ? 'Saving...' : 'Save and Apply'}
          </Button>
          <Button
            onClick={handleApplyFilters}
            disabled={!hasUnsavedChanges}
            className="flex-1"
          >
            Apply Filter
          </Button>
        </div>
      </div>

      {/* Save Filter Modal */}
      {pendingFilters && (
        <SaveFilterModal
          open={saveModalOpen}
          onOpenChange={setSaveModalOpen}
          pendingFilters={pendingFilters}
          existingFilters={savedFilters}
          onSave={handleSaveFilter}
          editingFilter={editingFilter}
        />
      )}
    </div>
  );
};
